import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import {
  isValidElement,
  type ReactNode,
  useContext,
  useEffect,
  useState
} from 'react'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { CloseTrigger } from '~/components/Global/Interactions/CloseTrigger/CloseTrigger'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import {
  sideAction,
  sideActionDetailList,
  sideActionDetailListHeader,
  sideActionDetailListHeaderCount,
  sideActionDetailListItem,
  sideActionDetailListWrapper,
  sideActionHeader,
  sideActionLogo,
  sideActionPicto,
  sideActionSubtitle,
  sideActionSubtitleLink,
  sideActionTitle,
  sideActionTitleWrapper
} from '~/components/Scopes/Actions/AsideAction.css'
import { useApplicantsHead } from '~/domains/applicants/queries/applicantHead'
import type { ApplicantHeadData } from '~/domains/applicants/types'
import { useContactsHead } from '~/domains/contacts/queries/useContactHead'
import type { ContactHeadData } from '~/domains/contacts/types'
import { themeClass } from '~/styles/theme.css'
import { toast } from '~/utils'
import { ApplicantModalContext, AsideActionContext } from '~/utils/contextes'
import { formatName } from '~/utils/names'
import { AsideActionsItems } from './AsideActionsItems'
import { useCloseAction } from './hooks/useCloseAction'

type AsideActionProps = {
  children: ReactNode | ReactNode[]
  title?: string
  icon?: IconDefinition
  logo?: string
  applicantIds?: number[]
  contactIds?: number[]
  onClose?: () => void
}

export const AsideAction = ({
  children,
  title,
  icon,
  logo,
  applicantIds,
  contactIds,
  onClose
}: AsideActionProps) => {
  const applicantDataHeadQueries = useApplicantsHead({
    applicantIds: applicantIds ?? []
  })
  const contactDataHeadQueries = useContactsHead({
    contactIds: contactIds ?? []
  })
  const dataHead = applicantIds
    ? applicantDataHeadQueries
        .map((query) => query.data)
        .filter((data) => data !== undefined && data !== null)
    : contactDataHeadQueries
        .map((query) => query.data)
        .filter((data) => data !== undefined && data !== null)
  const [listShown, setListShown] = useState(false)
  const { position } = useContext(AsideActionContext)
  const closeAction = useCloseAction()
  const hasActionsItems = Array.isArray(children)
    ? children.some(
        (child) => isValidElement(child) && child.type === AsideActionsItems
      )
    : isValidElement(children) && children.type === AsideActionsItems
  const isLoading =
    applicantDataHeadQueries.some((query) => query.isLoading) ||
    contactDataHeadQueries.some((query) => query.isLoading)
  const entityLabel = contactIds ? 'contact' : 'candidat'
  const ids = contactIds ?? applicantIds ?? []
  if (!isLoading && ids.length > 0 && dataHead.length === 0) {
    toast(
      'Accès refusé',
      `Vous n'avez pas le droit d'effectuer cette action sur ${ids?.length === 1 ? `ce ${entityLabel}` : `ces ${entityLabel}s`}.`,
      ToastType.Danger
    )
    closeAction()
    return
  }

  if (!isLoading && ids.length > dataHead.length) {
    toast(
      'Accès partiel',
      `Vous n'avez pas le droit d'effectuer cette action sur certains candidats de la sélection.`,
      ToastType.Warning
    )
  }
  return (
    <div className={clsx(sideAction, themeClass)}>
      {!hasActionsItems && (
        <CloseTrigger
          onClose={() => {
            closeAction().then((confirm) => {
              if (confirm) {
                onClose?.()
              }
            })
          }}
        />
      )}
      {title && (
        <div className={sideActionHeader}>
          <div className={sideActionTitleWrapper}>
            <h3 className={sideActionTitle}>
              {logo ? (
                <img className={sideActionLogo} src={logo} alt={title} />
              ) : (
                <>
                  {icon && (
                    <span className={sideActionPicto}>
                      <FontAwesomeIcon icon={icon} />
                    </span>
                  )}
                  {title}
                </>
              )}
            </h3>
            {position === 'sidebar' && ids && (
              <div className={sideActionSubtitle}>
                {!dataHead && ids.length > 0 && <Shimmer height={20} />}
                {dataHead &&
                  ids.length > 0 &&
                  dataHead.length < 3 &&
                  `${dataHead.map((person) => formatName(person)).join(', ')}`}
                {dataHead && dataHead.length >= 3 && (
                  <>
                    {dataHead
                      .map((person) => formatName(person))
                      .slice(0, 2)
                      .join(', ')}{' '}
                    et{' '}
                    <span
                      className={sideActionSubtitleLink}
                      onClick={() => setListShown(true)}
                    >
                      {dataHead.length - 2} autre
                      {dataHead.length - 2 > 1 ? 's' : ''}
                    </span>
                  </>
                )}
                <AnimatePresence>
                  {listShown && applicantIds && dataHead && (
                    <ApplicantList
                      applicants={dataHead as ApplicantHeadData[]}
                      onClose={() => setListShown(false)}
                      key="applicants-list"
                    />
                  )}
                  {listShown && contactIds && dataHead && (
                    <ContactList
                      contacts={dataHead as ContactHeadData[]}
                      onClose={() => setListShown(false)}
                      key="contacts-list"
                    />
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
          {hasActionsItems &&
            Array.isArray(children) &&
            children.filter(
              (child) =>
                isValidElement(child) && child.type === AsideActionsItems
            )}
        </div>
      )}
      {Array.isArray(children)
        ? children.filter(
            (child) =>
              !(isValidElement(child) && child.type === AsideActionsItems)
          )
        : children}
    </div>
  )
}

type ApplicantListProps = {
  applicants: ApplicantHeadData[]
  onClose: () => void
}

type ContactListProps = {
  contacts: ContactHeadData[]
  onClose: () => void
}

type PersonListProps = {
  persons: {
    id: number
    firstname?: string
    lastname?: string
  }[]
  setCurrentId?: (id: number | null) => void
  onClose: () => void
}

const ApplicantList = ({ applicants, onClose }: ApplicantListProps) => {
  const { setApplicantCollection, setApplicantCurrentId: setCurrentId } =
    useContext(ApplicantModalContext)

  useEffect(() => {
    setApplicantCollection(applicants)
  }, [setApplicantCollection, applicants])

  return (
    <PersonList
      persons={applicants}
      setCurrentId={setCurrentId}
      onClose={onClose}
    />
  )
}

const ContactList = ({ contacts, onClose }: ContactListProps) => {
  return <PersonList persons={contacts} onClose={onClose} />
}

const PersonList = ({ persons, setCurrentId, onClose }: PersonListProps) => {
  return (
    <motion.div
      className={sideActionDetailList}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <CloseTrigger onClose={onClose} />
      <div className={sideActionDetailListHeader}>
        Sélection{' '}
        <span className={sideActionDetailListHeaderCount}>
          ({persons?.length})
        </span>
      </div>
      <motion.div
        variants={{
          hidden: { opacity: 0 },
          visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
        }}
        initial="hidden"
        animate="visible"
        className={sideActionDetailListWrapper}
      >
        {persons?.map((person) => (
          <motion.div
            variants={{
              initial: { opacity: 0, x: -20 },
              hidden: { opacity: 0, x: -20 },
              visible: { opacity: 1, x: 0 }
            }}
            key={person.id}
            className={sideActionDetailListItem}
            onClick={() => {
              setCurrentId?.(person.id)
            }}
          >
            {formatName(person)}
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  )
}
